import { isPlatformBrowser } from '@angular/common';
import type {
	AfterViewInit,
	ElementRef,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	inject,
	Input,
	input,
	Output,
	PLATFORM_ID,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';

import dayjs from 'dayjs';
import flatpickr from 'flatpickr';
import FlatpickrLanguages from 'flatpickr/dist/l10n';
import type { Instance } from 'flatpickr/dist/types/instance';
import type { BaseOptions } from 'flatpickr/dist/types/options';

import { BaseDirective } from '@valk-nx/core/lib/directives/base/base.directive';
import { FlatpickrFacade } from '@valk-nx/flatpickr-store/flatpickr.facade';

export interface FlatPickrEvent {
	selectedDates: Date[];
	dateString: string;
	instance: Instance;
	type: FlatPickrEventType;
}

export enum FlatPickrEventType {
	OnChange = 'onChange',
	OnOpen = 'onOpen',
	OnReady = 'onReady',
	OnValueUpdate = 'onValueUpdate',
}
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: `vp-calendar`,
	styleUrls: ['calendar.scss'],
	templateUrl: './calendar.html',
	imports: [],
})
export class CalendarComponent
	extends BaseDirective
	implements AfterViewInit, OnChanges
{
	private readonly platformId = inject(PLATFORM_ID);
	private readonly flatpickrFacade = inject(FlatpickrFacade);

	@Input() calendarData: string[];
	@Input() config: Partial<BaseOptions> = {};
	@Input() isLoading = false;
	@Input() jumpToFirstAvailableDate = false;

	hideRangePrices = input<boolean>(false);
	blockCalendarJump = input<boolean>(false);

	@Output() emitDates = new EventEmitter<Date[]>();
	@Output() emitMonthChange = new EventEmitter<string>();
	@Output() datePickerEmitter = new EventEmitter<FlatPickrEvent>();
	@Output() dayCreate = new EventEmitter<{
		dayElement: HTMLElement;
		instance: Instance;
	}>();
	@Output() emitHasError = new EventEmitter<boolean>();

	@ViewChild('calendar') calendar: ElementRef;

	datePicker: Instance;
	loadingDiv: HTMLDivElement;

	constructor() {
		super();
	}

	ngAfterViewInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.initFlatPickr();
			this.handleLoading();
			this.handlePricingRangeVisibility();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (isPlatformBrowser(this.platformId)) {
			if (
				changes['config']?.currentValue?.minDate &&
				!changes['config'].firstChange
			) {
				this.initFlatPickr();
			}
			if (changes['calendarData']) {
				this.flatpickrFacade.redrawFlatpickr();
			}
			if (
				this.jumpToFirstAvailableDate &&
				changes['calendarData']?.currentValue
			) {
				const date = dayjs(
					changes['calendarData'].currentValue[0],
				).toDate();
				if (!this.blockCalendarJump()) {
					this.flatpickrFacade.jumpToDate(date);
				}
			}

			if (
				changes['config']?.currentValue?.defaultDate !==
				changes['config']?.previousValue?.defaultDate
			) {
				this.jumpToDateString(
					changes['config'].currentValue.defaultDate,
				);
			}
			this.handleLoading();
		}
	}

	jumpToDateString(dateString) {
		const date = dayjs(dateString).toDate();
		this.flatpickrFacade.setDatesAndUpdateInCalendar([date]);
		this.flatpickrFacade.jumpToDate(date);
	}

	initFlatPickr() {
		this.datePicker = flatpickr(this.calendar.nativeElement, {
			locale: { ...FlatpickrLanguages[this.language()] },
			maxDate: dayjs().add(1, 'year').toDate(),
			minDate: dayjs().subtract(1, 'day').toDate(),
			monthSelectorType: 'static',
			inline: true,
			mode: 'single',
			...this.config,
			/* v8 ignore start */
			onChange: (
				selectedDates: Date[],
				dateString: string,
				instance: Instance,
			) => {
				this.datePickerEmitter.emit({
					type: FlatPickrEventType.OnChange,
					dateString,
					instance,
					selectedDates,
				});
			},
			onMonthChange: (
				_selectedDates: Date[],
				_dateString: string,
				instance: Instance,
			) => {
				this.handleMonthChange(instance);
			},
			onOpen: (
				selectedDates: Date[],
				dateString: string,
				instance: Instance,
			) => {
				this.datePickerEmitter.emit({
					type: FlatPickrEventType.OnOpen,
					dateString,
					instance,
					selectedDates,
				});
			},
			onReady: (
				selectedDates: Date[],
				dateString: string,
				instance: Instance,
			) => {
				this.datePickerEmitter.emit({
					type: FlatPickrEventType.OnReady,
					dateString,
					instance,
					selectedDates,
				});
			},
			/* v8 ignore stop */
			onDayCreate: (
				_selectedDates: Date[],
				_dateString: string,
				instance: Instance,
				dayElement: HTMLElement,
			) => {
				this.dayCreate.emit({ dayElement, instance });
				this.handleDayCreate(dayElement);
			},
			/* v8 ignore start */
			onValueUpdate: (
				selectedDates: Date[],
				dateString: string,
				instance: Instance,
			) => {
				this.datePickerEmitter.emit({
					type: FlatPickrEventType.OnValueUpdate,
					dateString,
					instance,
					selectedDates,
				});
			},
			/* v8 ignore stop */
		});
		this.flatpickrFacade.setInstance(this.datePicker);
	}

	handleDayCreate(dayElement: HTMLElement) {
		if (
			dayElement['dateObj'] &&
			!dayElement.classList.contains('flatpickr-disabled') &&
			Array.isArray(this.calendarData)
		) {
			const date = dayjs(dayElement['dateObj']).format('YYYY-MM-DD');

			if (!this.calendarData.includes(date)) {
				dayElement.classList.add('flatpickr-disabled');
			}
		}
	}

	handleMonthChange(instance: Instance): void {
		const { currentMonth, currentYear } = instance;
		const newDate = dayjs(`${currentYear}-${currentMonth + 1}-01`);
		const minDate = dayjs(instance.config.minDate).add(1, 'day');

		if (!dayjs(minDate).isBefore(newDate)) {
			this.emitMonthChange.emit(minDate.format('YYYY-MM-DD'));
			return;
		}
		this.emitMonthChange.emit(newDate.format('YYYY-MM-DD'));
	}

	private createLoadingElements(): void {
		this.loadingDiv = document.createElement('div');
		this.loadingDiv.classList.add('loading-container');
		this.loadingDiv.setAttribute('id', 'is-loading');

		const loadingSVG = document.createElement('div');
		loadingSVG.classList.add('loading-svg');
		this.loadingDiv.append(loadingSVG);
	}

	handleLoading(): void {
		if (!this.datePicker) {
			return;
		}

		if (this.isLoading) {
			if (!this.loadingDiv) {
				this.createLoadingElements();
			}

			this.datePicker.calendarContainer.classList.add('is-loading');
			this.datePicker.innerContainer.append(this.loadingDiv);
		} else {
			this.datePicker.calendarContainer.classList.remove('is-loading');

			if (this.loadingDiv) {
				this.loadingDiv.remove();
			}
		}
	}

	handlePricingRangeVisibility(): void {
		if (!this.datePicker) {
			return;
		}

		if (this.hideRangePrices()) {
			this.datePicker.calendarContainer.classList.add(
				'hide-range-prices',
			);
		} else {
			this.datePicker.calendarContainer.classList.remove(
				'hide-range-prices',
			);
		}
	}
}
